/**
 * Homesteads — player land ownership + building. Core data model + pure geometry.
 *
 * Schema-first, like rpg.ts / ships.ts: the persisted/wire records and the
 * deterministic spatial math live here so the server can validate and every
 * client agrees. Everything in this file is PURE and pmath-safe (no trig; only
 * integer/float arithmetic, Math.sqrt/abs/min/max) so it runs identically on the
 * server and every client. Rendering + build-mode UX are client-side.
 *
 * Coordinates are ALWAYS planet-local metres (invariant to a planet's orbit and
 * spin — the CharacterHometownV1 precedent), converted to world for rendering via
 * the planet's live pose. Design: docs/superpowers/specs/2026-07-17-homesteads-design.md
 */
import type { ItemStack } from "./../rpg.js";
import { faceDir } from "./../cubesphere.js";
import {
  LEVEL_REGION,
  cellCenterDir,
  cellsPerFace,
  dirToCell,
  packCell,
  type CellId,
} from "./../worldgen/cells.js";

export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number];

/** A claim ward. Planting one owns the sphere `|p − anchor| ≤ radius`; paying to
 *  raise `tier` grows the radius + budgets. All coords planet-local metres. */
export interface Beacon {
  /** Server-minted, stable, planet-scoped (safe-encoded for a Firestore doc id). */
  id: string;
  ownerUid: string;
  /** Stable PlanetDef.id — NOT an array index (orbit-safe, resolves via planetById). */
  planetId: string;
  anchor: Vec3;
  radius: number;
  /** Index into TIERS; caps radius, pieceBudget, editBudget together. */
  tier: number;
  /** uids permitted to build/edit (the owner is always implicitly permitted). */
  allowlist: string[];
  createdAt: number;
}

/** One placed atomic building piece. `t` indexes the homestead piece catalog
 *  (shared/src/homestead/pieces.ts, Phase 2); transform is fully free (any quat). */
export interface Piece {
  t: number;
  p: Vec3;
  q: Quat;
  /** Material/tint choice (4-slot palette convention). */
  mat: number;
}

/** A piece as the server stores/broadcasts it: `pid` is a per-beacon,
 *  server-minted monotonic id — the stable demolish handle (an array index
 *  would shift under concurrent removals). */
export interface PlacedPiece extends Piece {
  pid: number;
}

/** A bounded terrain-edit raster for one region cell of one beacon (Phase 3). The
 *  raster covers only the beacon footprint, so storage scales with claim size —
 *  never planet size. `delta` is stored stringified/base64. */
export interface TerrainEdit {
  beaconId: string;
  /** packCell this raster serves. */
  cell: number;
  /** Samples per metre. */
  res: number;
  origin: Vec3;
  dim: [number, number];
  /** Content hash — meshing/collision gate on client/server agreement. */
  hash: string;
  delta: Float32Array;
}

// --- Tiers: one number gates radius + budgets, so per-beacon storage is bounded
// by what the player paid for. `cost` is CUMULATIVE credits to reach the tier
// (claim = TIERS[0].cost; expanding a→b pays cost[b] − cost[a]). Indexes ≥
// PUBLIC_MAX_TIER (see registry) are home-world-only. Numbers are illustrative and
// tunable per the spec's open questions. ---

export interface TierDef {
  /** Claim sphere radius, metres. */
  radius: number;
  /** Max placed pieces. */
  pieceBudget: number;
  /** Max terrain-edit raster cells across the claim (Phase 3). */
  editBudget: number;
  /** Cumulative credit cost to reach this tier. */
  cost: number;
}

export const TIERS: readonly TierDef[] = [
  { radius: 24, pieceBudget: 150, editBudget: 25_000, cost: 500 },
  { radius: 48, pieceBudget: 400, editBudget: 100_000, cost: 2_000 },
  { radius: 96, pieceBudget: 1_000, editBudget: 400_000, cost: 8_000 },
  { radius: 192, pieceBudget: 2_500, editBudget: 1_600_000, cost: 30_000 },
];

export const MAX_TIER = TIERS.length - 1;

/** Clamp a raw tier index into range, then return its def. */
export function tierDef(tier: number): TierDef {
  const t = Math.min(MAX_TIER, Math.max(0, Math.floor(tier)));
  return TIERS[t];
}

/** Credits to expand from tier `from` to tier `to` (0 if not an increase). */
export function expandCost(from: number, to: number): number {
  const a = Math.min(MAX_TIER, Math.max(0, Math.floor(from)));
  const b = Math.min(MAX_TIER, Math.max(0, Math.floor(to)));
  return b > a ? TIERS[b].cost - TIERS[a].cost : 0;
}

// --- Piece cost: the "pay materials per piece" sink. A piece's build-material
// tier maps to existing catalog materials (ids from rpg.ts ITEMS). ---

export type BuildMaterialTier = "wood" | "stone" | "metal" | "glass";

const PIECE_COST: Readonly<Record<BuildMaterialTier, readonly ItemStack[]>> = {
  wood: [{ id: "scrap-alloy", n: 2 }],
  stone: [{ id: "ore-ferrite", n: 3 }],
  metal: [{ id: "ore-ferrite", n: 2 }, { id: "scrap-alloy", n: 2 }],
  glass: [{ id: "exotic-crystal", n: 1 }],
};

/** Materials one piece of the given build tier costs (fresh copies, safe to mutate). */
export function pieceCost(tier: BuildMaterialTier): ItemStack[] {
  return PIECE_COST[tier].map((s) => ({ id: s.id, n: s.n }));
}

/**
 * The star-system id a planet id belongs to — beacon.planetId is the stable
 * PlanetDef.id (`hyperlight:sys:x:y:z:planet:n[...]` or
 * `hyperlight:home:<uid>:planet:n[...]`). Null for unrecognized ids. Pure
 * string parsing (the "my homesteads" roster + warp targets need the system).
 */
export function systemIdOfPlanetId(planetId: string): string | null {
  const parts = planetId.split(":");
  if (parts[0] !== "hyperlight") return null;
  if (parts[1] === "sys" && parts.length >= 5) return `${parts[2]}:${parts[3]}:${parts[4]}`;
  if (parts[1] === "home" && parts.length >= 3 && parts[2]) return `home:${parts[2]}`;
  return null;
}

// --- Validation of untrusted persisted/wire data (Firestore docs, client msgs) ---

function validVec3(v: unknown): Vec3 | null {
  return Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === "number" && Number.isFinite(n))
    ? [v[0] as number, v[1] as number, v[2] as number]
    : null;
}

/**
 * Validate an untrusted beacon (a Firestore doc or a wire payload). Returns a
 * sanitized copy or null if structurally hopeless. The allowlist defaults to empty
 * (a dropped entry must not reject the whole beacon); tier must be in range.
 */
export function validateBeacon(raw: unknown): Beacon | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== "string" || !o.id) return null;
  if (typeof o.ownerUid !== "string" || !o.ownerUid) return null;
  if (typeof o.planetId !== "string" || !o.planetId) return null;
  const anchor = validVec3(o.anchor);
  if (!anchor) return null;
  if (typeof o.radius !== "number" || !Number.isFinite(o.radius) || o.radius <= 0) return null;
  if (typeof o.tier !== "number" || !Number.isInteger(o.tier) || o.tier < 0 || o.tier > MAX_TIER) return null;
  const allowlist = Array.isArray(o.allowlist)
    ? o.allowlist.filter((u): u is string => typeof u === "string")
    : [];
  const createdAt = typeof o.createdAt === "number" && Number.isFinite(o.createdAt) ? o.createdAt : 0;
  return { id: o.id, ownerUid: o.ownerUid, planetId: o.planetId, anchor, radius: o.radius, tier: o.tier, allowlist, createdAt };
}

function validQuat(v: unknown): Quat | null {
  return Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number" && Number.isFinite(n))
    ? [v[0] as number, v[1] as number, v[2] as number, v[3] as number]
    : null;
}

/** Validate an untrusted piece (wire payload). Catalog-range checking of `t` is
 *  the SERVER's job (it knows the live catalog length); here we only demand a
 *  non-negative int so a newer catalog never fails an older validator. */
export function validatePiece(raw: unknown): Piece | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.t !== "number" || !Number.isInteger(o.t) || o.t < 0) return null;
  const p = validVec3(o.p);
  const q = validQuat(o.q);
  if (!p || !q) return null;
  const mat = typeof o.mat === "number" && Number.isInteger(o.mat) && o.mat >= 0 ? o.mat : 0;
  return { t: o.t, p, q, mat };
}

/** Validate an untrusted stored/broadcast piece (adds the pid handle). */
export function validatePlacedPiece(raw: unknown): PlacedPiece | null {
  const base = validatePiece(raw);
  if (!base) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.pid !== "number" || !Number.isInteger(o.pid) || o.pid < 0) return null;
  return { ...base, pid: o.pid };
}

// --- Pure geometry (planet-local metres; squared-distance comparisons, no trig) ---

function dist2(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const dx = ax - bx, dy = ay - by, dz = az - bz;
  return dx * dx + dy * dy + dz * dz;
}

/** Is planet-local point `p` inside the beacon's claim sphere? */
export function pointInBeacon(b: Beacon, p: Vec3): boolean {
  return dist2(p[0], p[1], p[2], b.anchor[0], b.anchor[1], b.anchor[2]) <= b.radius * b.radius;
}

/**
 * The exact circumradius (metres) of a region cell: the max distance from its
 * surface centre to any of its four corners. Computed from the corner directions
 * (not a flat half-diagonal — the cube→sphere projection stretches edge cells, so
 * a flat estimate under-reads and would MISS touched cells).
 */
function cellCircumradius(planetRadius: number, cell: CellId): number {
  const n = cellsPerFace(planetRadius, cell.level);
  const u0 = -1 + (2 * cell.x) / n, u1 = -1 + (2 * (cell.x + 1)) / n;
  const v0 = -1 + (2 * cell.y) / n, v1 = -1 + (2 * (cell.y + 1)) / n;
  const cc: Vec3 = [0, 0, 0];
  cellCenterDir(planetRadius, cell, cc);
  const cx = cc[0] * planetRadius, cy = cc[1] * planetRadius, cz = cc[2] * planetRadius;
  const d: Vec3 = [0, 0, 0];
  let max2 = 0;
  const corners: readonly [number, number][] = [[u0, v0], [u1, v0], [u0, v1], [u1, v1]];
  for (const [u, v] of corners) {
    faceDir(cell.face, u, v, d);
    const m2 = dist2(d[0] * planetRadius, d[1] * planetRadius, d[2] * planetRadius, cx, cy, cz);
    if (m2 > max2) max2 = m2;
  }
  return Math.sqrt(max2);
}

/**
 * Does the beacon overlap the given region cell? Conservative (inclusive): a cell
 * is covered when its surface centre is within `radius + cellCircumradius` of the
 * anchor, so the registry never MISSES a touched cell (over-inclusion only ever
 * streams an empty cell). All planet-local metres; squared-distance only.
 *
 * NOTE: this is deliberately loose for a spatial INDEX. Claim-vs-claim overlap is
 * an exact anchor-to-anchor sphere test (registry.ts), not this.
 */
export function beaconCoversCell(planetRadius: number, b: Beacon, cell: CellId): boolean {
  const cc: Vec3 = [0, 0, 0];
  cellCenterDir(planetRadius, cell, cc);
  const reach = b.radius + cellCircumradius(planetRadius, cell);
  return dist2(cc[0] * planetRadius, cc[1] * planetRadius, cc[2] * planetRadius,
    b.anchor[0], b.anchor[1], b.anchor[2]) <= reach * reach;
}

/**
 * The region cells (packCell keys) a beacon registers into — the reverse-index
 * keys used for streaming + overlap. Samples the anchor + a rim ring of directions
 * (seam-correct via dirToCell) and keeps the cells the beacon actually covers.
 * Assumes radius ≤ one region cell (holds for every TIER); larger claims are made
 * of many beacons per the spec.
 */
export function cellsForBeacon(planetRadius: number, b: Beacon): number[] {
  const [ax, ay, az] = b.anchor;
  const len = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
  const ad: Vec3 = [ax / len, ay / len, az / len];
  // Tangent basis about the anchor direction (no trig).
  const helper: Vec3 = Math.abs(ad[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const t1 = normalize(cross(helper, ad));
  const t2 = cross(ad, t1); // unit (ad ⟂ t1)
  const reach = b.radius / len; // offset in unit-vector space ≈ angular reach
  const out = new Set<number>();
  const add = (dx: number, dy: number, dz: number): void => {
    const cell = dirToCell(planetRadius, LEVEL_REGION, dx, dy, dz);
    if (beaconCoversCell(planetRadius, b, cell)) out.add(packCell(cell));
  };
  add(ad[0], ad[1], ad[2]); // anchor
  const RIM: readonly [number, number][] = [
    [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];
  for (const [su, sv] of RIM) {
    const dir = normalize([
      ad[0] + reach * (su * t1[0] + sv * t2[0]),
      ad[1] + reach * (su * t1[1] + sv * t2[1]),
      ad[2] + reach * (su * t1[2] + sv * t2[2]),
    ]);
    add(dir[0], dir[1], dir[2]);
  }
  // The anchor's own cell is always registered, even if the disk test is marginal.
  out.add(packCell(dirToCell(planetRadius, LEVEL_REGION, ad[0], ad[1], ad[2])));
  return [...out];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function normalize(v: Vec3): Vec3 {
  const l = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}
